import React from 'react'
import type { JSX } from 'react'
import { Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAppAuth'
import { consumeLoggingOut } from '@/lib/logout-flag'

/**
 * Connect login route.
 *
 * The app no longer renders its own sign-in card. Cognito's Hosted UI is
 * itself fully branded (same logo, pipe-system background, and dark theme),
 * so the previous "click Sign In to go and sign in" screen was a redundant,
 * near-identical hop. This route normally redirects straight to the Hosted UI,
 * where Cognito renders username/password (non-acme pools) or Azure AD SSO
 * (acme) based on the per-pool config:
 *
 * - authenticated      → go to the dashboard
 * - just signed out    → show "you've been signed out" with a manual Sign In
 * - still initializing → show a brief loading state
 * - unauthenticated    → kick off signinRedirect() exactly once
 *
 * The "just signed out" case is deliberate: logout sets a one-shot marker (see
 * lib/logout-flag) BEFORE redirecting to Cognito's /logout. If we auto-redirected
 * here instead, the signinRedirect() navigation would cancel the in-flight
 * /logout request, Cognito's session would never clear, and the user would be
 * logged straight back in. Skipping the auto-redirect lets /logout win, and the
 * explicit screen confirms the sign-out succeeded.
 *
 * The /login route is kept as this thin shell so existing links and
 * ProtectedRoute's default loginPath ('/login') keep working.
 */
export const LoginPage = (): JSX.Element => {
  const { isAuthenticated, isLoading, signinRedirect } = useAuth()
  const [error, setError] = React.useState<string | null>(null)
  const redirectStarted = React.useRef(false)
  // Read-and-clear the logout marker once, on first render. Held in state so a
  // later re-render (after the marker is gone) can't flip us into auto-redirect.
  const [signedOut] = React.useState<boolean>(consumeLoggingOut)

  const startSignIn = React.useCallback((): void => {
    setError(null)
    signinRedirect().catch((err: unknown): void => {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
    })
  }, [signinRedirect])

  React.useEffect(() => {
    // Wait for auth to settle, then redirect once for unauthenticated users.
    // The ref guard prevents a second redirect on re-render / StrictMode.
    // Never auto-redirect right after a logout — that would cancel /logout.
    if (isLoading || isAuthenticated || signedOut || redirectStarted.current) return
    redirectStarted.current = true
    startSignIn()
  }, [isLoading, isAuthenticated, signedOut, startSignIn])

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center space-y-4">
        {error ? (
          <>
            <p className="text-sm text-destructive">{error}</p>
            <Button onClick={startSignIn}>Try again</Button>
          </>
        ) : signedOut ? (
          <>
            <p className="text-muted-foreground">You've been signed out.</p>
            <Button onClick={startSignIn}>Sign In</Button>
          </>
        ) : (
          <p className="text-muted-foreground">
            {isLoading ? 'Loading…' : 'Redirecting to sign in…'}
          </p>
        )}
      </div>
    </div>
  )
}
